// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useOrganizationsStore from '@/app/store/modules/organizations';
import actionsOrganizations from '../../api/actionsOrganizations';
// Components
import DataTable from '@/common-utils/components/renders/dataTable/DataTable';
// Common
import { columnsOrganizations, columnsOrganizationsId } from './columnDef/columnsOrganizations';
import getInitialQuery from '@/common-utils/components/renders/dataTable/utils/getInitialQuery';
import mergeQuery from '@/common-utils/components/renders/dataTable/utils/mergeQuery';
import Permissions from '@/app/permissions/Permissions';
import { ORGANIZATION } from '../../api/apiUrls';

/**
 * @param  root0
 * @param  root0.customQuery
 * @param  root0.singleSelection
 * @param root0.rowsSelected
 * @param root0.setRowsSelected
 * @param root0.organizationId
 * @param root0.pageActions
 * @param root0.filtersEventName
 * @returns {JSX}
 */
function OrganizationsTable({
    customQuery = {},
    singleSelection = false,
    rowsSelected = undefined,
    setRowsSelected = undefined,
    organizationId = undefined,
    pageActions = undefined,
    filtersEventName
}) {
    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const { refetchOrganizations: refetchFromStore, storeRefetchOrganizations, query: queryStored, storeQuery } = useOrganizationsStore();

    // Constants
    const initialQuery = getInitialQuery(customQuery);
    const selectionMode = !!(rowsSelected && setRowsSelected);

    // State
    const [query, setQuery] = useState(mergeQuery(initialQuery, queryStored));
    const [launchRefetch, setLaunchRefetch] = useState(true);

    // Api Calls
    const { data, isFetching, error, refetch } = actionsOrganizations.getOrganizations({ query: query, organizationId: organizationId || rootOrganization.uuid }, false);

    // Hooks
    useEffect(() => {
        if (rootOrganization && (launchRefetch || refetchFromStore)) {
            refetch();
            setLaunchRefetch(false);
            storeRefetchOrganizations(false);
        }
    }, [query, launchRefetch, refetchFromStore]);

    const allColumns = columnsOrganizations({ query, setQuery, queryStored, storeQuery, setLaunchRefetch, selectionMode, singleSelection, filtersEventName });

    // Render
    return (
        <DataTable
            filtersEventName={filtersEventName}
            columns={allColumns}
            data={data}
            dataFetchingIsLoading={isFetching}
            error={error}
            refetch={refetch}
            columnsId={columnsOrganizationsId}
            initialQuery={initialQuery}
            query={query}
            setQuery={setQuery}
            queryStored={queryStored}
            storeQuery={storeQuery}
            setLaunchRefetch={setLaunchRefetch}

            selectionMode={selectionMode}
            singleSelection={singleSelection}
            rowsSelected={rowsSelected}
            setRowsSelected={setRowsSelected}

            pageActions={pageActions}
            // Hide export on details page
            exportProps={!organizationId ? {
                permissions: [Permissions.EXPORT_ORGANIZATIONS.name],
                api: ORGANIZATION.EXPORT
            } : null
            }
        />
    );
}

export default OrganizationsTable;

OrganizationsTable.propTypes = {
    customQuery: PropTypes.object,
    singleSelection: PropTypes.bool,
    rowsSelected: PropTypes.object,
    setRowsSelected: PropTypes.func,
    organizationId: PropTypes.string,
    pageActions: PropTypes.node,
    filtersEventName: PropTypes.string
};